/**
 * DailyTimeline - Single day view with hourly timeline
 * More detailed view of scheduled workouts for one day
 *
 * Refactored: Touch-friendly targets (44px min), mobile improvements
 */

import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { parseISO, isToday, format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { Clock } from 'lucide-react';
import { useFitnessStore } from '../../stores/fitnessStore';
import TimeSlotBlock from './TimeSlotBlock';

const HOURS = Array.from({ length: 15 }, (_, i) => i + 8); // 08:00 - 22:00

const DailyTimeline = () => {
  const {
    selectedDay,
    scheduledWorkouts,
    workouts,
    selectScheduledWorkoutForEdit,
    openDayDetailModal,
  } = useFitnessStore();

  const selectedDate = parseISO(selectedDay);
  const isTodaySelected = isToday(selectedDate);

  // Get scheduled workouts for selected day
  const dayScheduledWorkouts = useMemo(() => {
    return scheduledWorkouts.filter(w => w.scheduled_date === selectedDay);
  }, [scheduledWorkouts, selectedDay]);

  // Get completed workouts for selected day
  const dayCompletedWorkouts = useMemo(() => {
    return workouts.filter(w => w.date === selectedDay);
  }, [workouts, selectedDay]);

  // Calculate unique exercises count
  const uniqueExerciseCount = useMemo(() => {
    const unique = new Set(dayCompletedWorkouts.map(w => w.exercise_name));
    return unique.size;
  }, [dayCompletedWorkouts]);

  // Group by hour
  const scheduledByHour = useMemo(() => {
    const map = {};
    dayScheduledWorkouts.forEach(workout => {
      const hour = parseInt(workout.scheduled_time?.split(':')[0] || '0');
      if (!map[hour]) map[hour] = [];
      map[hour].push(workout);
    });
    return map;
  }, [dayScheduledWorkouts]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="bg-gray-900/50 border border-gray-800 rounded-xl overflow-hidden"
    >
      {/* Day header */}
      <div className={`p-4 border-b border-gray-800 ${isTodaySelected ? 'bg-orange-500/10' : 'bg-gray-800/50'}`}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className={`text-lg font-semibold ${isTodaySelected ? 'text-orange-400' : 'text-white'}`}>
              {isTodaySelected ? 'Bugün - ' : ''}{format(selectedDate, 'd MMMM EEEE', { locale: tr })}
            </h2>
            <div className="flex items-center gap-3 mt-1 text-sm">
              <span className="text-blue-400">
                {uniqueExerciseCount} Egzersiz
              </span>
              <span className="text-gray-600">•</span>
              <span className="text-green-400">
                {dayCompletedWorkouts.length} Set
              </span>
            </div>
          </div>

          {/* Quick view completed workouts - touch-friendly */}
          {dayCompletedWorkouts.length > 0 && (
            <button
              onClick={() => openDayDetailModal(selectedDay, dayCompletedWorkouts)}
              className="px-4 py-2.5 min-h-touch bg-green-600/20 text-green-400 rounded-lg text-sm font-medium hover:bg-green-600/30 active:bg-green-600/40 transition-colors"
            >
              Tamamlananları Gör
            </button>
          )}
        </div>
      </div>

      {/* Timeline */}
      <div className="max-h-[calc(100vh-350px)] sm:max-h-[calc(100vh-300px)] overflow-y-auto">
        {HOURS.map(hour => {
          const hourWorkouts = scheduledByHour[hour] || [];
          const currentHour = new Date().getHours();
          const isCurrentHour = isTodaySelected && hour === currentHour;

          return (
            <div
              key={hour}
              className={`flex border-b border-gray-800/50 min-h-[72px] sm:min-h-[80px] ${
                isCurrentHour ? 'bg-orange-500/5' : ''
              }`}
            >
              {/* Time label */}
              <div className={`w-16 sm:w-20 flex-shrink-0 p-2 sm:p-3 border-r border-gray-800 flex items-start justify-center ${
                isCurrentHour ? 'text-orange-400' : 'text-gray-500'
              }`}>
                <div className="flex items-center gap-1 text-xs sm:text-sm">
                  <Clock className="w-3 h-3" />
                  {hour.toString().padStart(2, '0')}:00
                </div>
              </div>

              {/* Workout slot */}
              <div className="flex-1 p-2 sm:p-3 relative min-h-touch">
                {hourWorkouts.length > 0 && (
                  <div className="space-y-2">
                    {hourWorkouts.map(workout => (
                      <TimeSlotBlock
                        key={workout.id}
                        workout={workout}
                        expanded
                        onClick={() => selectScheduledWorkoutForEdit(workout)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </motion.div>
  );
};

export default DailyTimeline;
